import {
  BEAUTY_VARIANT_VALUES,
  FASHION_VARIANT_VALUES,
  INDUSTRY_CATEGORY_SUGGESTIONS,
  NUTRITION_VARIANT_VALUES,
  SHOE_VARIANT_VALUES
} from './industry-category-suggestions';

/**
 * Variant options (and the values each takes) offered for each industry's categories, keyed by option name.
 * Standard option types (Color, Size, Material, Style, Gender, Length) keep their own value lists and are not listed here.
 * Free-text rows in the source sheet (Model, Dimensions) are left out.
 */
const OTHER_INDUSTRY_VARIANT_VALUES: { [industry: string]: { [option: string]: string[] } } = {
  'Vitamins & Supplements': {
    Form: [
      'Capsule', 'Tablet', 'Softgel', 'Gummy', 'Powder', 'Liquid', 'Ampoule', 'Caplet', 'Capsules', 'Chewable tablets',
      'Drops', 'Effervescent tablets', 'Gel', 'Granules', 'Lozenges', 'Oil', 'Pill', 'Spray', 'Syrup', 'Tablets', 'Other'
    ],
    Strength: ['500 mg', '1000 mg', '1000 IU', '5000 IU'],
    Count: ['30', '60', '90', '120', '180'],
    Flavor: [
      'Orange', 'Berry', 'Lemon', 'Unflavored', 'Almond', 'Apple', 'Banana', 'Blueberry', 'Caramel', 'Cherry', 'Cinnamon',
      'Coconut', 'Coffee', 'Dulce de leche', 'Elderflower', 'Ginger', 'Chocolate', 'Lime', 'Lychee', 'Mango', 'Matcha',
      'Mint', 'Mojito', 'Peach', 'Raspberry', 'Rose', 'Salted caramel', 'Strawberry', 'Tropical', 'Vanilla', 'Other'
    ],
    'Pack Size': ['1', '2', '3'],
    'Dietary Type': [
      'Vegan', 'Gluten-Free', 'Non-GMO', 'Alcohol-free', 'Dairy-free', 'Decaffeinated', 'Halal', 'High protein', 'Keto',
      'Kosher', 'Lactose-free', 'Low fat', 'Low sodium', 'Mountain shade grown', 'No added MSG', 'No artificial colors',
      'No artificial flavors', 'No artificial sweeteners', 'No preservatives', 'Nut-free', 'Organic', 'Paleo',
      'Single origin', 'Soy-free', 'Sugar-free', 'Vegetarian', 'Wholegrain', 'Other'
    ],
    'Age Group': [
      'Adult', 'Kids', 'Senior', '0-6 months', '6-12 months', '1-2 years', 'Adults', 'All ages', 'Babies', 'Newborn',
      'Teens', 'Toddlers', 'Universal', 'Other'
    ]
  },
  'Electronics & Accessories': {
    'Storage Capacity': ['64 GB', '128 GB', '256 GB', '512 GB', '1 TB'],
    RAM: ['4 GB', '8 GB', '16 GB', '32 GB'],
    'Screen Size': ['13 in', '15 in', '24 in', '32 in', '55 in'],
    Connectivity: ['Wired', 'Bluetooth', 'Wi-Fi'],
    Condition: ['New', 'Refurbished', 'Used'],
    Warranty: ['6 Months', '1 Year', '2 Years']
  },
  'Mobile & Tech Accessories': {
    'Compatible Model': ['iPhone 15', 'Galaxy S24', 'Pixel 8', 'Universal'],
    'Cable Length': ['1 m', '2 m', '3 m'],
    'Capacity (mAh)': ['5000', '10000', '20000'],
    'Connector Type': [
      'USB-C', 'Lightning', 'Micro-USB', 'AC adapter', 'AirPlay', 'ANT', 'ANT+', 'AUX', 'Bluetooth', 'BNC', 'Cellular',
      'Coaxial', 'Coaxial (F-Type)', 'Composite video', 'DECT', 'Display port', 'DVI', 'Ethernet', 'Firewire', 'HDMI',
      'Infrared', 'LTE', 'Micro SD card', 'Mini USB', 'Miracast', 'NFC', 'Power cord', 'Power splitter', 'Radio', 'RCA',
      'S-video', 'Satellite', 'SD card', 'Thunderbolt', 'USB', 'VGA', 'Wi-Fi', 'Wired', 'Wireless', 'Wireless dongle', 'Other'
    ],
    Wattage: ['10 W', '20 W', '45 W', '65 W'],
    'Pack Size': ['1', '2', '3']
  },
  'Jewelry & Accessories': {
    'Chain Length': ['16"', '18"', '20"', '24"'],
    'Ring Size': ['5', '6', '7', '8', '9', '10', '11', '12'],
    Stone: ['Diamond', 'Pearl', 'Crystal', 'None']
  },
  'Toys & Games': {
    'Age Range': ['0-2', '3-5', '6-8', '9-12', '13+'],
    'Theme / Character': ['Animals', 'Vehicles', 'Princess', 'Superhero'],
    'Piece Count': ['50', '100', '500', '1000'],
    Players: ['1', '2', '2-4', '4+']
  },
  'Books & Stationery': {
    Language: ['English', 'Urdu', 'Arabic', 'Spanish'],
    Format: ['Paperback', 'Hardcover', 'eBook'],
    'Ink Color': ['Black', 'Blue', 'Red'],
    'Paper Size': ['A4', 'A5', 'Letter', 'Legal'],
    'Page Count': ['100', '200', '300'],
    Ruling: ['Lined', 'Plain', 'Grid'],
    'Pack Size': ['1', '5', '10', '12']
  },
  'Sports & Fitness': {
    Weight: ['5 lb', '10 lb', '20 lb', '50 lb'],
    'Resistance Level': ['Light', 'Medium', 'Heavy'],
    Thickness: ['4 mm', '6 mm', '8 mm']
  },
  'Home & Furniture': {
    Finish: ['Matte', 'Glossy', 'Natural'],
    'Seating Capacity': ['1', '2', '3', '4', '6'],
    'Mattress Size': ['Twin', 'Full', 'Queen', 'King']
  },
  'Kitchen & Dining': {
    Capacity: ['0.5 L', '1 L', '2 L', '5 L'],
    'Set Size': ['1', '4', '6', '12 Pieces'],
    Wattage: ['500 W', '800 W', '1200 W', '1500 W'],
    Finish: ['Matte', 'Glossy', 'Brushed']
  },
  'Grocery & Food': {
    Weight: ['250 g', '500 g', '1 kg', '5 kg'],
    'Pack Size': ['1', '6', '12', '24'],
    Flavor: ['Original', 'Spicy', 'Sweet', 'Plain'],
    Volume: ['250 ml', '500 ml', '1 L', '2 L'],
    Type: ['Organic', 'Regular'],
    'Fat Content': ['Full Fat', 'Low Fat', 'Skim'],
    Dietary: ['Halal', 'Vegan', 'Gluten-Free']
  },
  'Pet Supplies': {
    'Pet Type': ['Dog', 'Cat', 'Bird', 'Fish', 'Small Pet'],
    Flavor: ['Chicken', 'Beef', 'Fish', 'Lamb'],
    Weight: ['1 kg', '5 kg', '10 kg', '20 kg'],
    'Life Stage': ['Puppy/Kitten', 'Adult', 'Senior'],
    'Breed Size': ['Small', 'Medium', 'Large'],
    'Pack Size': ['1', '6', '12']
  },
  'Tools & Hardware': {
    Voltage: ['12 V', '18 V', '20 V', '110 V', '220 V'],
    'Power Source': ['Corded', 'Cordless', 'Manual'],
    'Pack Size': ['1', '10', '50', '100'],
    Grit: ['60', '120', '220']
  },
  'Gifts & Specialty Items': {
    Occasion: ['Birthday', 'Wedding', 'Eid', 'Christmas', 'Anniversary'],
    Theme: ['Floral', 'Classic', 'Kids', 'Luxury'],
    Personalization: ['None', 'Name', 'Message', 'Photo'],
    'Pack Size': ['1', '3', '6', '12'],
    Recipient: ['Him', 'Her', 'Kids', 'Couple']
  },
  'Arts & Crafts': {
    'Pack Size': ['6', '12', '24', '48'],
    Medium: ['Acrylic', 'Watercolor', 'Oil', 'Pencil'],
    'Quality Grade': ['Student', 'Artist', 'Professional'],
    'Paper Weight': ['90 gsm', '150 gsm', '300 gsm'],
    Shade: ['Light', 'Dark', 'Neon', 'Pastel']
  },
  'Home Décor & Lifestyle': {
    'Set Size': ['1', '2', '3', '4'],
    Scent: ['Lavender', 'Vanilla', 'Sandalwood', 'Unscented'],
    Shape: ['Round', 'Square', 'Rectangle'],
    Finish: ['Matte', 'Glossy', 'Antique']
  },
  'Sporting Goods': {
    Sport: ['Cricket', 'Football', 'Basketball', 'Tennis', 'Golf'],
    Weight: ['Light', 'Standard', 'Heavy'],
    'Age Group': ['Kids', 'Youth', 'Adult'],
    'Hand Orientation': ['Left', 'Right']
  },
  'Grocery & Convenience': {
    Flavor: ['Original', 'Chocolate', 'Salted', 'Spicy', 'Mint'],
    Weight: ['50 g', '100 g', '250 g', '500 g'],
    'Pack Size': ['1', '6', '12', '24'],
    Volume: ['330 ml', '500 ml', '1 L', '2 L'],
    Type: ['Regular', 'Sugar-Free', 'Diet'],
    Scent: ['Lemon', 'Fresh', 'Lavender', 'Unscented'],
    Quantity: ['10', '25', '50', '100 Count'],
    Dietary: ['Halal', 'Vegan', 'Gluten-Free']
  },
  'Handmade & Artisan Products': {
    Customization: ['None', 'Name', 'Initials', 'Custom Design'],
    Scent: ['Lavender', 'Rose', 'Sandalwood', 'Unscented'],
    'Set Size': ['1', '2', '4', '6']
  }
};

/** Every industry's variant options, including the ones defined in the industry-category-suggestions module. */
export const VARIANT_VALUES_BY_INDUSTRY: { [industry: string]: { [option: string]: string[] } } = {
  'Fashion & Clothing': FASHION_VARIANT_VALUES,
  'Shoes & Footwear': SHOE_VARIANT_VALUES,
  'Beauty & Cosmetics': BEAUTY_VARIANT_VALUES,
  'Nutrition & Wellness': NUTRITION_VARIANT_VALUES,
  ...OTHER_INDUSTRY_VARIANT_VALUES
};

/** Standard option types keep their own value lists, so they are never treated as fixed-value options. */
const STANDARD_VARIANT_NAMES = ['color', 'colour', 'size', 'material', 'style', 'gender', 'target gender', 'length'];

const fixedVariantNames = new Set<string>();
Object.keys(VARIANT_VALUES_BY_INDUSTRY).forEach((industry) => {
  Object.keys(VARIANT_VALUES_BY_INDUSTRY[industry]).forEach((option) => fixedVariantNames.add(option.toLowerCase()));
});

/** True when the name is one of the fixed-value options of any industry (and not a standard option type). */
export function isFixedVariantName(name: string | null | undefined): boolean {
  const key = String(name || '').trim().toLowerCase();
  if (!key || STANDARD_VARIANT_NAMES.indexOf(key) >= 0) return false;
  return fixedVariantNames.has(key);
}

/**
 * The industry a category belongs to: the industry's own name, or the industry whose sub category or product it is.
 * Returns null when the category is none of these (for example the user's own categories).
 */
export function industryForCategory(categoryName: string | null | undefined): string | null {
  const wanted = String(categoryName || '').trim().toLowerCase();
  if (!wanted) return null;

  const named = Object.keys(VARIANT_VALUES_BY_INDUSTRY).find((industry) => industry.toLowerCase() === wanted);
  if (named) return named;

  const industry = Object.keys(INDUSTRY_CATEGORY_SUGGESTIONS).find((name) => {
    const subCategories = INDUSTRY_CATEGORY_SUGGESTIONS[name];
    return Object.keys(subCategories).some((subCategory) =>
      subCategory.toLowerCase() === wanted || subCategories[subCategory].some((product) => product.toLowerCase() === wanted)
    );
  });
  return industry ?? null;
}

/** The fixed-value options (and values) for the category's industry; empty for other categories. */
export function variantValuesFor(categoryName: string | null | undefined): { [option: string]: string[] } {
  const industry = industryForCategory(categoryName);
  return (industry && VARIANT_VALUES_BY_INDUSTRY[industry]) || {};
}

/** Every option name from every industry, each once. */
export function allVariantNames(): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  Object.keys(VARIANT_VALUES_BY_INDUSTRY).forEach((industry) => {
    Object.keys(VARIANT_VALUES_BY_INDUSTRY[industry]).forEach((option) => {
      const key = option.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      names.push(option);
    });
  });
  return names;
}

/** The option names offered in the Recommended list for the category's industry. */
export function variantNamesFor(categoryName: string | null | undefined): string[] {
  return Object.keys(variantValuesFor(categoryName));
}
